import { useEffect, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  EquipIcon,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  NavItem,
  Text,
  useMediaQuery,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  GroupChannelListOrder,
  MyMemberStateFilter,
  PublicChannelFilter,
  UnreadChannelFilter,
} from '@sendbird/chat/groupChannel';
import { ChannelListProvider } from '@sendbird/uikit-react/ChannelList/context';

import MaudLogo from '~/assets/svg/MaudLogo';
import { SIDE_NAV_WIDTH, SIDE_NAV_WIDTH_SMALL } from '~/lib/constants';
import { VITE_MAUD_APP_VERSION } from '~/lib/constants/env';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { UserMode } from '~/lib/contexts/ModeContext';
import { useSendbirdReadyContext } from '~/lib/contexts/SendbirdReadyContext';
import { useTaskContext } from '~/lib/contexts/TaskContext';
import { isMedicationOrderEligibleProviderType } from '~/lib/hooks/usePatientTabsCollection';
import useSupportChat from '~/lib/hooks/useSupportChat';
import { PRESCRIPTION_MESSAGES_COUNT_POLLING_INTERVAL_MS } from '~/components/maud/prescriptions/prescriptions.constants';
import { getAuthorizedLinks, isPathMatch } from '~/lib/util/nav.util';
import { useGetScriptSurePrescriptionMessagesCountQuery } from '~/store/services/integration';
import util from '~/lib/util/util';
import { Path } from '~/routes/constants';

import AccountMenu from './AccountMenu';
import {
  NAV_BOTTOM_PADDING,
  NAV_HORIZONTAL_PADDING,
  NAV_HORIZONTAL_PADDING_SMALL,
  NAV_SEARCHBAR_PADDING_TOP,
  NAV_SEARCHBAR_PADDING_TOP_SMALL,
  NAV_TOP_PADDING,
} from './constants';
import GlobalSearch from './GlobalSearch';
import MessagesMenu from './MessagesMenu/MessagesMenu';
import NotificationsMenu from './Notification/NotificationsMenu';
import AddNewCta from '../AddNewCta';

const {
  secondary: { 5: secondaryFive },
} = EquipUIFireflyV2Theme.colors;

interface MaudLinkDefinition {
  alias?: Path;
  allow: UserMode[];
  iconName: FAIconName | null;
  id: string;
  label: string;
  path: Path;
}

const MAUD_LINK_DEFINITIONS: MaudLinkDefinition[] = [
  {
    allow: [UserMode.MAUD],
    iconName: FAIconName.homeRegular,
    id: 'dashboard',
    label: 'Dashboard',
    path: Path.MAUD,
  },
  {
    allow: [UserMode.MAUD],
    iconName: FAIconName.checkSquareRegular,
    id: 'tasks',
    label: 'Tasks',
    path: Path.MAUD_TASKS,
  },
  {
    allow: [UserMode.MAUD],
    iconName: FAIconName.userRegular,
    id: 'patients',
    label: 'Patients',
    path: Path.MAUD_PATIENTS,
  },
  {
    allow: [UserMode.MAUD],
    iconName: FAIconName.fileImport,
    id: 'inquiries',
    label: 'Inquiries',
    path: Path.MAUD_INQUIRIES,
  },
  {
    allow: [UserMode.MAUD],
    iconName: FAIconName.pillsSolid,
    id: 'prescriptions',
    label: 'Prescriptions',
    path: Path.MAUD_PRESCRIPTIONS,
  },
  {
    allow: [UserMode.MAUD],
    iconName: FAIconName.analyticsSolid,
    id: 'analytics',
    label: 'Analytics',
    path: Path.MAUD_ANALYTICS,
  },
  {
    allow: [],
    iconName: null,
    id: 'settings',
    label: 'Settings',
    path: Path.SETTINGS,
  },
  {
    alias: Path.CALENDAR,
    allow: [UserMode.MAUD],
    iconName: FAIconName.calendarRegular,
    id: 'calendar',
    label: 'Calendar',
    path: Path.CALENDAR_SCHEDULE,
  },
];

const sortedLinkDefinitions = [...MAUD_LINK_DEFINITIONS].sort(
  (a, b) => b.path.split('/').length - a.path.split('/').length,
);

const MaudNav = () => {
  const { currentPatientId, currentUser, permissions, roles } =
    useGlobalContext();
  const { setComponentVisibility } = useComponentPortalContext();
  const { sdk: sendBirdSdkInstance } = useSendbirdReadyContext();
  const {
    eprescribeIntegration: isEprescribeIntegrationEnabled,
    maudIntercomChatMvp: isMaudIntercomChatMvpEnabled,
    maudProviderChatMessagesPrototype: isMessagesMenuEnabled,
  } = useFlags<LDFlags>();

  const { myOpenTasksCount } = useTaskContext();
  const canViewPrescriptions =
    isEprescribeIntegrationEnabled &&
    isMedicationOrderEligibleProviderType(
      currentUser?.providerTypeDetails?.name,
    );
  const { currentData: prescriptionMessagesCount } =
    useGetScriptSurePrescriptionMessagesCountQuery(undefined, {
      pollingInterval: PRESCRIPTION_MESSAGES_COUNT_POLLING_INTERVAL_MS,
      skip: !canViewPrescriptions,
    });

  const getNavItemCounter = (id: string): number | null => {
    if (id === 'tasks') return myOpenTasksCount;
    if (id === 'prescriptions') return prescriptionMessagesCount?.count ?? null;
    return null;
  };

  const history = useHistory();
  const { pathname } = history.location;

  const selectedNavItem = sortedLinkDefinitions.find(({ alias, path }) =>
    isPathMatch(alias ?? path, pathname),
  )?.id;
  const hasPatientSearchPermission =
    permissions[MaudPermissions.MAUD_PATIENT_SEARCH];
  const [isMessagesMenuOpen, setIsMessagesMenuOpen] = useState<boolean>(false);
  const [isNotificationsMenuOpen, setIsNotificationsMenuOpen] =
    useState<boolean>(false);
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState<boolean>(false);

  const [isScreenWidthLessThan1280px] = useMediaQuery('(max-width: 1280px)');
  const isSmall = isScreenWidthLessThan1280px;

  const SIDE_NAV_WIDTH_COMPUTED = isSmall
    ? SIDE_NAV_WIDTH_SMALL
    : SIDE_NAV_WIDTH;

  const channelListQueries = useMemo(() => {
    return {
      channelListQuery: {
        includeEmpty: false,
        includeFrozen: false,
        limit: 50,
        myMemberStateFilter: MyMemberStateFilter.JOINED,
        order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
        publicChannelFilter: PublicChannelFilter.ALL,
        unreadChannelFilter: UnreadChannelFilter.ALL,
      },
    };
  }, []);

  useEffect(() => {
    setComponentVisibility({
      component: PortalComponent.BILLING_PERIOD_ACCORDION,
      data: null,
      isExpanded: false,
      isVisible: false,
    });
  }, [currentPatientId]);

  const authorizedLinks = useMemo<MaudLinkDefinition[]>(
    () =>
      (
        getAuthorizedLinks(
          MAUD_LINK_DEFINITIONS,
          UserMode.MAUD,
          roles,
        ) as MaudLinkDefinition[]
      ).filter(({ id }) => id !== 'prescriptions' || canViewPrescriptions),
    [roles, canViewPrescriptions],
  );

  const { openSupportChat } = useSupportChat();

  return (
    <ChakraFlex
      as="nav"
      backgroundColor={secondaryFive}
      direction="column"
      height="-webkit-fill-available"
      padding={`${NAV_TOP_PADDING}px ${
        isSmall ? NAV_HORIZONTAL_PADDING_SMALL : NAV_HORIZONTAL_PADDING
      }px ${NAV_BOTTOM_PADDING}px`}
      width={SIDE_NAV_WIDTH_COMPUTED}
      zIndex={
        isMessagesMenuOpen || isNotificationsMenuOpen || isSearchPanelOpen
          ? 101
          : 100
      }
    >
      <ChakraFlex
        justifyContent="left"
        onClick={() => history.push(Path.MAUD)}
        paddingLeft={!isSmall && '6px'}
        style={{ cursor: 'pointer' }}
      >
        {isSmall ? <EquipIcon height="30px" width="65px" /> : <MaudLogo />}
      </ChakraFlex>
      {hasPatientSearchPermission && (
        <ChakraFlex
          paddingLeft={!isSmall && '6px'}
          paddingTop={`${
            isSmall
              ? NAV_SEARCHBAR_PADDING_TOP_SMALL
              : NAV_SEARCHBAR_PADDING_TOP
          }px`}
          width={`calc(${SIDE_NAV_WIDTH_COMPUTED} - 58px)`}
          zIndex={5}
        >
          <GlobalSearch
            isSmall={isSmall}
            onSearchPanelVisibilityChange={(isSearchPanelOver: boolean) => {
              setIsSearchPanelOpen(isSearchPanelOver);
            }}
          />
        </ChakraFlex>
      )}
      <ChakraBox marginTop="16px" px={isSmall ? '4px' : '8px'}>
        <AddNewCta isSmall={isSmall} />
      </ChakraBox>
      <ChakraFlex
        direction="column"
        height="-webkit-fill-available"
        justifyContent="space-between"
        paddingTop="32px"
      >
        <ChakraFlex
          direction="column"
          gap="12px"
          px={isSmall ? '4px' : '8px'}
          sx={{ '& > *': { cursor: 'pointer' } }}
        >
          {authorizedLinks.map(({ label, id, iconName, path }) => (
            <NavItem
              counter={getNavItemCounter(id)}
              iconName={iconName}
              isSelected={selectedNavItem === id}
              isSmall={isSmall}
              key={`NavItem__${id}`}
              label={label}
              onClick={() => history.push(path)}
              tooltipText={label}
            />
          ))}
        </ChakraFlex>
        <ChakraBox>
          {isMessagesMenuEnabled && sendBirdSdkInstance && (
            <ChannelListProvider queries={channelListQueries}>
              <MessagesMenu
                isSmall={isSmall}
                onMessagesMenuVisibilityChange={(isOpen: boolean) =>
                  setIsMessagesMenuOpen(isOpen)
                }
              />
            </ChannelListProvider>
          )}
          <NotificationsMenu
            isSmall={isSmall}
            onNotificationsMenuVisibilityChange={(
              isNotificationOver: boolean,
            ) => setIsNotificationsMenuOpen(isNotificationOver)}
          />
          {isMaudIntercomChatMvpEnabled && isSmall && (
            <ChakraBox px="4px">
              <NavItem
                iconName={FAIconName.headsetSolid}
                isSelected={false}
                isSmall
                label="Support"
                onClick={openSupportChat}
                tooltipText="Support"
              />
            </ChakraBox>
          )}
          <AccountMenu isSmall={isSmall} />
          <ChakraFlex
            justifyContent="space-between"
            marginTop={isSmall ? undefined : '12px'}
          >
            {isMaudIntercomChatMvpEnabled && !isSmall && (
              <FireflyButton
                id="nav-menu__open-support-chat"
                leftIcon={FAIconName.headsetSolid}
                onClick={openSupportChat}
                size="small"
                variant="accent.tertiary"
              >
                Support
              </FireflyButton>
            )}
            {VITE_MAUD_APP_VERSION && (
              <Text
                align={isSmall ? 'center' : undefined}
                color="secondary.50"
                variant={isSmall ? 'overline' : 'caption'}
              >
                {util.getAppVersion()}
              </Text>
            )}
          </ChakraFlex>
        </ChakraBox>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default MaudNav;
